import React, { useState, useEffect, useMemo } from 'react';
import {
  Zap,
  Play,
  Pause,
  Clock,
  AlertTriangle,
  ShieldCheck,
  CheckCircle2,
  RefreshCw,
  Calendar,
  ArrowRight,
  Bell,
  Sparkles,
  Filter,
  ChevronRight,
  Activity,
  Cpu,
  Layers,
  Terminal,
  Server,
  Smartphone,
  Mail,
  FileText,
  Check,
  X,
  ShieldAlert,
  Eye,
  Sliders,
  DollarSign,
  AlertCircle,
  ExternalLink,
  MessageSquare,
  Building2,
  Trash2,
  Users,
  Search,
  Copy,
  Send,
  CheckCheck,
  Info,
  GitBranch,
  ArrowDown,
  CornerDownRight,
  Handshake,
  Gavel
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { WhatsAppApiService } from '@/services/whatsappApiService';

export interface SchedulerSettings {
  copropiedad_id: string;
  nombre_copropiedad: string;
  nit: string;
  administrador_nombre: string;
  email_contacto: string;
  telefono_contacto: string;
  cuenta_bancaria_info: string;
  dia_corte_mes: number;
  frecuencia_notificacion: string;
  umbrales_dias_mora: number[];
  canales_habilitados: string[];
  respetar_ley_2300: boolean;
  tasa_interes_tipo: string;
  modo_seleccion_propietarios?: 'todos' | 'manual';
  propietarios_seleccionados?: string[];
}

export interface DebtorCandidate {
  copropiedad_id?: string;
  copropiedad_nombre?: string;
  direccion?: string;
  unidad_privada: string;
  propietario_nombre: string;
  propietario_id?: string;
  telefono?: string;
  email?: string;
  saldo_capital: number;
  saldo_intereses: number;
  saldo_total?: number;
  dias_mora: number;
  etapa_cobro: string;
  periodos_adeudados?: string[];
  payment_link?: string;
}

interface CobranzaSchedulerHubProps {
  settings: SchedulerSettings;
  onSaveSettings: (newSettings: SchedulerSettings) => Promise<void>;
  registeredDebtors: DebtorCandidate[];
  onTriggerBatchSend?: (items: DebtorCandidate[]) => Promise<void>;
  onOpenWhatsAppConfig?: () => void;
}

const SCHEDULES_STORAGE_KEY = 'cartera_ph_saved_schedules_v1';

const getInitialSchedules = (currentSettings: SchedulerSettings): SchedulerSettings[] => {
  try {
    const raw = localStorage.getItem(SCHEDULES_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Error reading saved schedules from localStorage:', e);
  }

  if (currentSettings && currentSettings.nombre_copropiedad) {
    return [currentSettings];
  }
  return [];
};

// Generador de plantillas de mensaje según la etapa procesal de cobranza PH
export function generateCarteraPhMessageTemplate(
  stage: 'preventivo' | 'prejuridico' | 'prejudicial' | 'judicial',
  data: {
    copropiedad_nombre: string;
    nit?: string;
    propietario_nombre: string;
    unidad_privada: string;
    saldo_capital: number;
    saldo_intereses: number;
    saldo_total: number;
    dias_mora: number;
    payment_link?: string;
    cuenta_bancaria_info?: string;
    administrador_nombre?: string;
    email_contacto?: string;
    telefono_contacto?: string;
  }
) {
  const ph = data.copropiedad_nombre || 'Copropiedad Residencial';
  const nit = data.nit || 'NIT No Registrado';
  const prop = data.propietario_nombre || 'Estimado(a) Propietario(a)';
  const unidad = data.unidad_privada || 'Unidad Inmobiliaria';
  const totalFmt = (data.saldo_total || 0).toLocaleString('es-CO');
  const capFmt = (data.saldo_capital || 0).toLocaleString('es-CO');
  const intFmt = (data.saldo_intereses || 0).toLocaleString('es-CO');
  const dias = data.dias_mora || 0;
  const link = data.payment_link || 'https://api.miasistentelegalia.com/portal-deudor';
  const banco = data.cuenta_bancaria_info || 'Consulte cuenta oficial con Administración';
  const admin = data.administrador_nombre || 'Administración PH';
  const email = data.email_contacto || 'administracion@copropiedad.com';
  const tel = data.telefono_contacto || '3001234567';

  switch (stage) {
    case 'preventivo':
      return {
        stageName: 'Etapa 1: Preventiva & Pronto Pago (1 a 30 Días)',
        badgeColor: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300',
        tone: 'Cordial y Amistoso',
        legalBasis: 'Ley 2300 de 2023 ("Dejen de Fregar") • Notificación hábil de recordatorio oportuno.',
        whatsapp:
`🏢 *${ph.toUpperCase()}*
*Aviso Preventivo de Cobranza de Expensas Comunes*

Estimado(a) propietario(a) *${prop}*, inmueble *${unidad}*:

Le extendemos un cordial saludo desde la Administración. Le recordamos amablemente que su unidad presenta un saldo pendiente de expensas comunes por valor de:
💰 *TOTAL A PAGAR: $${totalFmt} COP* (${dias} días de mora).

Lo invitamos a realizar su pago para conservar sus beneficios de descuento por pronto pago y estar al día con su copropiedad:

💳 *Pague en línea seguro por PSE / Tarjeta aquí:*
${link}

🏦 *O transfiera a la cuenta oficial de la copropiedad:*
${banco}

Por favor remita su comprobante a ${email}. Si ya realizó su pago en las últimas 24 horas, agradecemos hacer caso omiso a este mensaje.
_Notificación enviada en horario hábil legal conforme a la Ley 2300 de 2023._`,
        emailSubject: `Recordatorio Cordial de Pago Expensas Comunes - Inmueble ${unidad} - ${ph}`,
        emailBody:
`Apreciado(a) ${prop},

Esperamos se encuentre muy bien. La Administración de ${ph} se permite recordarle el estado actual de su cuenta por concepto de cuotas de administración correspondientes al inmueble ${unidad}.

A la fecha, registra un saldo pendiente de $${totalFmt} COP (${dias} días de mora).

Para su comodidad, puede pagar en 30 segundos mediante nuestra pasarela en línea PSE:
Enlace de Pago PSE: ${link}

Información Bancaria de Recaudo:
${banco}

Agradecemos enviar su comprobante de pago al correo ${email}.

Cordialmente,
${admin}
${ph} | Tel: ${tel}`
      };

    case 'prejuridico':
      return {
        stageName: 'Etapa 2: Prejurídico Formal Art. 48 (31 a 60 Días)',
        badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300',
        tone: 'Formal y Vinculante',
        legalBasis: 'Ley 675 de 2001 Art. 48 • Mérito Ejecutivo de certificaciones expedidas por el Administrador.',
        whatsapp:
`⚖️ *NOTIFICACIÓN FORMAL DE COBRO PREJURÍDICO*
*CONFORME AL ARTÍCULO 48 DE LA LEY 675 DE 2001*

*Copropiedad:* ${ph} (NIT: ${nit})
*Destinatario:* ${prop}
*Inmueble:* ${unidad}

Estimado(a) propietario(a):
Le notificamos formalmente que a la fecha presenta una obligación vencida y legalmente exigible por expensas comunes de administración:
• Saldo Capital: $${capFmt} COP
• Intereses Moratorios certificados (SFC): $${intFmt} COP
💵 *TOTAL LIQUIDADO EN MORA: $${totalFmt} COP* (${dias} días de mora).

Conforme al Art. 48 de la Ley 675 de 2001, las certificaciones de saldo expedidas por el Administrador prestan *MÉRITO EJECUTIVO* ante la jurisdicción ordinaria civil sin necesidad de previo requerimiento judicial.

Evite el cobro adicional de honorarios profesionales de abogado y el reporte respectivo cancelando de inmediato:
🔗 *Botón Oficial de Pago en Línea PSE / Wompi:*
${link}

🤝 Si requiere facilidades de pago, puede suscribir un *Acuerdo de Pago formal* en línea comunicándose a ${tel} o ${email}.`,
        emailSubject: `Requerimiento Prejurídico Formal Art. 48 Ley 675 - Unidad ${unidad} - ${ph}`,
        emailBody:
`NOTIFICACIÓN PREJURÍDICA FORMAL
Régimen de Propiedad Horizontal - Ley 675 de 2001

Señor(a):
${prop}
Propietario(a) Inmueble ${unidad}
${ph} - NIT ${nit}

Por medio de la presente, la Administración le requiere formalmente para la cancelación de las expensas comunes en mora, liquidadas con estricta sujeción a la ley:

• Saldo de Capital: $${capFmt} COP
• Intereses Moratorios causados a tasa Superfinanciera: $${intFmt} COP
• SALDO TOTAL EXIGIBLE: $${totalFmt} COP (${dias} días acumulados).

De conformidad con el Artículo 48 de la Ley 675 de 2001, el presente estado de cuenta constituye TÍTULO EJECUTIVO pleno para cobro judicial. 

Le exhortamos a normalizar su obligación en el término perentorio de cinco (5) días hábiles a través de nuestra pasarela de pagos:
${link}

Atentamente,
${admin}
Administración y Representación Legal
${ph}`
      };

    case 'prejudicial':
      return {
        stageName: 'Etapa 3: Prejudicial & Aviso Previo a Demanda (61 a 90 Días)',
        badgeColor: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300',
        tone: 'Perentorio y Riguroso',
        legalBasis: 'Código General del Proceso Art. 599 • Intimación previa y solicitud de medidas cautelares.',
        whatsapp:
`🚨 *INTIMACIÓN PREJUDICIAL PERENTORIA - AVISO PREVIO A DEMANDA*
*COPROPIEDAD: ${ph.toUpperCase()}*

*DEUDOR(A):* ${prop}
*UNIDAD PRIVADA:* ${unidad}
*SALDO TOTAL EJECUTABLE:* $${totalFmt} COP (${dias} días de mora)

Por medio de la presente se le otorga un *PLAZO PERENTORIO DE CINCO (5) DÍAS HÁBILES* a partir del recibo de este mensaje para cancelar la totalidad de la deuda o suscribir un acuerdo transaccional vinculante con la Administración.

⚠️ *ADVERTENCIA DE COBRO JUDICIAL Y MEDIDAS CAUTELARES:*
Vencido dicho plazo sin pago, el expediente pasará a los apoderados judiciales para radicar *DEMANDA EJECUTIVA* ante los Juzgados Civiles Municipales, solicitando simultáneamente:
1. *EMBARGO Y RETENCIÓN* de sus cuentas bancarias corrientes y de ahorros (Art. 599 C.G.P.).
2. *EMBARGO Y SECUESTRO* del bien inmueble ${unidad} y sus parqueaderos.
3. Condena en costas y honorarios de cobranza judicial (20% adicional sobre el capital).

Normalice inmediatamente su obligación para suspender las acciones judiciales:
🔗 *PAGO EN LÍNEA INMEDIATO PSE:*
${link}

Contacto Jurídico Directo: ${tel} | ${email}`,
        emailSubject: `URGENTE: Intimación Prejudicial Perentoria y Aviso de Medidas Cautelares - Inmueble ${unidad}`,
        emailBody:
`INTIMACIÓN PREJUDICIAL PERENTORIA CON AVISO DE DEMANDA
Expediente de Cartera en Mora - Cobro Prejudicial

DEUDOR(A): ${prop}
INMUEBLE: ${unidad}
VALOR TOTAL EXIGIBLE: $${totalFmt} COP
DÍAS EN MORA: ${dias} días

Se le comunica que ha vencido cualquier plazo ordinario para la atención de sus expensas comunes con ${ph}. Se le otorga un término improrrogable de CINCO (5) DÍAS HÁBILES para realizar el pago total o suscribir un acuerdo extrajudicial.

De no efectuarse el pago, se radicará Demanda Ejecutiva ante los Juzgados Civiles solicitando las MEDIDAS CAUTELARES de embargo y secuestro sobre el inmueble ${unidad} y el embargo de cuentas bancarias de conformidad con el Art. 599 del C.G.P.

Enlace Oficial PSE para evitar radicación judicial:
${link}

Dirección Jurídica y Administración
${ph}`
      };

    case 'judicial':
    default:
      return {
        stageName: 'Etapa 4: Cobro Judicial (>90 Días)',
        badgeColor: 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300 border-purple-300',
        tone: 'Judicial / Procesal',
        legalBasis: 'Demanda Ejecutiva Singular de Mínima/Menor Cuantía • Juzgados Civiles Municipales.',
        whatsapp:
`⚖️ *EXPEDIENTE REMITIDO A COBRO JUDICIAL*
*Copropiedad:* ${ph}
*Unidad:* ${unidad} - *Propietario:* ${prop}
*Saldo en Demanda:* $${totalFmt} COP

Le informamos que debido a la mora acumulada superior a 90 días, su expediente ha sido trasladado al apoderado judicial de la copropiedad para la radicación de Demanda Ejecutiva ante los Juzgados Civiles.

Para evitar la formalización del embargo de cuentas bancarias e inmueble, comuníquese de inmediato con la Administración a ${tel} o realice su pago de emergencia en:
${link}`,
        emailSubject: `Notificación de Traslado a Cobro Judicial - Unidad ${unidad} - ${ph}`,
        emailBody:
`NOTIFICACIÓN DE TRASLADO A COBRO JUDICIAL

Señor(a) ${prop},
Inmueble ${unidad}

Su obligación por valor de $${totalFmt} COP ha sido enviada al área jurídica para radicación formal de demanda ejecutiva con solicitud de medidas cautelares en juzgado civil.

Enlace de pago de emergencia: ${link}
Contacto: ${tel}`
      };
  }
}

export const CobranzaSchedulerHub: React.FC<CobranzaSchedulerHubProps> = ({
  settings,
  onSaveSettings,
  registeredDebtors,
  onTriggerBatchSend,
  onOpenWhatsAppConfig
}) => {
  // Navegación de pestañas internas para descomprimir y ordenar la vista
  const [activeSubTab, setActiveSubTab] = useState<'config' | 'debtors' | 'messages' | 'pipeline'>('config');

  // Estado local de parámetros
  const [localSettings, setLocalSettings] = useState<SchedulerSettings>(settings);
  const [isSaving, setIsSaving] = useState(false);

  // Lista de programaciones guardadas por copropiedad
  const [savedSchedules, setSavedSchedules] = useState<SchedulerSettings[]>(() =>
    getInitialSchedules(settings)
  );

  // Copropiedad seleccionada en el dropdown
  const [selectedCopropiedad, setSelectedCopropiedad] = useState<string>(
    settings.nombre_copropiedad || ''
  );

  // Motor de Automatización (Inspirado en Celery Beat / Cron de Legal Scrapper)
  const [isEngineActive, setIsEngineActive] = useState<boolean>(() => {
    const saved = localStorage.getItem('scheduler_engine_active');
    return saved !== null ? saved === 'true' : true;
  });
  const [cronHour, setCronHour] = useState<string>('08:30');
  const [cronCadence, setCronCadence] = useState<'daily' | 'thresholds' | 'cut_day'>('thresholds');

  // Modal de Simulación (Dry-Run)
  const [isDryRunOpen, setIsDryRunOpen] = useState(false);
  const [isExecutingNow, setIsExecutingNow] = useState(false);

  // Modal de Previsualización Rápida de Mensaje para un deudor específico
  const [previewDebtor, setPreviewDebtor] = useState<DebtorCandidate | null>(null);

  // Selección de deudores para el Cron Job
  const [selectedDebtorKeys, setSelectedDebtorKeys] = useState<Set<string>>(() => {
    if (
      settings.propietarios_seleccionados &&
      Array.isArray(settings.propietarios_seleccionados) &&
      settings.propietarios_seleccionados.length > 0
    ) {
      return new Set(settings.propietarios_seleccionados);
    }
    // Por defecto, incluir a los deudores con mora >= 5 días
    return new Set(registeredDebtors.filter((d) => d.dias_mora >= 5).map((d) => d.unidad_privada));
  });

  // Modo de selección: 'todos' o 'manual'
  const [modoSeleccion, setModoSeleccion] = useState<'todos' | 'manual'>(
    settings.modo_seleccion_propietarios || 'todos'
  );

  // Búsqueda y filtros para la lista de propietarios
  const [debtorSearchQuery, setDebtorSearchQuery] = useState('');
  const [debtorStageFilter, setDebtorStageFilter] = useState<'all' | 'preventivo' | 'prejuridico' | 'prejudicial'>('all');

  // Pestaña de etapa seleccionada en el visualizador de mensajes
  const [selectedMessageStage, setSelectedMessageStage] = useState<'preventivo' | 'prejuridico' | 'prejudicial' | 'judicial'>('prejuridico');
  const [messageDebtorSelected, setMessageDebtorSelected] = useState<string>('sample');
  const [messageChannelView, setMessageChannelView] = useState<'whatsapp' | 'email'>('whatsapp');

  // Nodo activo en el Flujo Interactivo de Cobranza (Pipeline)
  const [selectedFlowNode, setSelectedFlowNode] = useState<'node1' | 'node2' | 'node3' | 'node4'>('node2');

  // Sincronizar estado cuando cambien props externas
  useEffect(() => {
    setLocalSettings(settings);
    if (settings.nombre_copropiedad && !selectedCopropiedad) {
      setSelectedCopropiedad(settings.nombre_copropiedad);
    }
    if (settings.modo_seleccion_propietarios) {
      setModoSeleccion(settings.modo_seleccion_propietarios);
    }
    if (settings.propietarios_seleccionados && settings.propietarios_seleccionados.length > 0) {
      setSelectedDebtorKeys(new Set(settings.propietarios_seleccionados));
    }
  }, [settings]);

  // Lista única de copropiedades disponibles para el selector
  const availableCopropiedades = useMemo(() => {
    const set = new Set<string>();
    if (settings.nombre_copropiedad) set.add(settings.nombre_copropiedad.trim());
    registeredDebtors.forEach((d) => {
      if ((d as any).copropiedad_nombre) set.add((d as any).copropiedad_nombre.trim());
    });
    savedSchedules.forEach((s) => {
      if (s.nombre_copropiedad) set.add(s.nombre_copropiedad.trim());
    });
    return Array.from(set).filter(Boolean);
  }, [settings, registeredDebtors, savedSchedules]);

  // Cálculo en tiempo real de la ventana hábil según Ley 2300 de 2023 ("Dejen de Fregar")
  const legalWindowStatus = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const timeVal = hour + minute / 60;

    let isOpen = false;
    let windowText = '';
    let nextOpening = '';

    if (day === 0) {
      isOpen = false;
      windowText = 'Domingo no hábil (Prohibido por Ley 2300)';
      nextOpening = 'Lunes 07:00 AM';
    } else if (day >= 1 && day <= 5) {
      if (timeVal >= 7.0 && timeVal < 19.0) {
        isOpen = true;
        windowText = 'Ventana Hábil Abierta (Lunes a Viernes 7:00 AM - 7:00 PM)';
        nextOpening = 'Cierre hoy a las 07:00 PM';
      } else if (timeVal < 7.0) {
        isOpen = false;
        windowText = 'Horario nocturno / temprano restringido';
        nextOpening = 'Abre hoy a las 07:00 AM';
      } else {
        isOpen = false;
        windowText = 'Horario nocturno restringido';
        nextOpening = day === 5 ? 'Sábado a las 08:00 AM' : 'Mañana a las 07:00 AM';
      }
    } else if (day === 6) {
      if (timeVal >= 8.0 && timeVal < 15.0) {
        isOpen = true;
        windowText = 'Ventana Hábil Sábados Abierta (8:00 AM - 3:00 PM)';
        nextOpening = 'Cierre hoy a las 03:00 PM';
      } else {
        isOpen = false;
        windowText = 'Fuera de horario legal de sábado';
        nextOpening = 'Lunes a las 07:00 AM';
      }
    }

    return { isOpen, windowText, nextOpening };
  }, []);

  // Multi-Queue Priority Routing Stats
  const queueStats = useMemo(() => {
    let high = 0;
    let highSaldo = 0;
    let medium = 0;
    let mediumSaldo = 0;
    let low = 0;
    let lowSaldo = 0;

    registeredDebtors.forEach((d) => {
      const tot = d.saldo_total || (d.saldo_capital + d.saldo_intereses);
      if (d.dias_mora > 60 || tot >= 2500000) {
        high++;
        highSaldo += tot;
      } else if (d.dias_mora >= 31) {
        medium++;
        mediumSaldo += tot;
      } else if (d.dias_mora >= 1) {
        low++;
        lowSaldo += tot;
      }
    });

    return {
      highCount: high,
      highSaldo,
      mediumCount: medium,
      mediumSaldo,
      lowCount: low,
      lowSaldo,
      totalDue: high + medium + low,
      totalDebt: highSaldo + mediumSaldo + lowSaldo
    };
  }, [registeredDebtors]);

  // Lista de candidatos para Simulación (Dry-Run) con mora relevante
  const dryRunCandidates = useMemo(() => {
    return registeredDebtors
      .filter((d) => d.dias_mora >= 5)
      .map((d) => {
        const tot = d.saldo_total || (d.saldo_capital + d.saldo_intereses);
        let targetStage = 'Preventivo';
        let stageCode: 'preventivo' | 'prejuridico' | 'prejudicial' | 'judicial' = 'preventivo';
        let recommendedChannel = 'WhatsApp';
        let priority = 'Normal';

        if (d.dias_mora > 90) {
          targetStage = 'Cobro Judicial CGP';
          stageCode = 'judicial';
          recommendedChannel = 'Expediente Judicial';
          priority = 'Jurídica';
        } else if (d.dias_mora > 60) {
          targetStage = 'Aviso Prejudicial / Demanda';
          stageCode = 'prejudicial';
          recommendedChannel = 'WhatsApp + Email (Dual)';
          priority = 'Crítica (Queue High)';
        } else if (d.dias_mora >= 31) {
          targetStage = 'Prejurídico Formal (Art. 48)';
          stageCode = 'prejuridico';
          recommendedChannel = 'WhatsApp + Email';
          priority = 'Media (Queue Medium)';
        } else {
          targetStage = 'Preventivo Pronto Pago';
          stageCode = 'preventivo';
          recommendedChannel = d.telefono ? 'WhatsApp' : 'Email';
          priority = 'Baja (Queue Low)';
        }

        const isSelected = selectedDebtorKeys.has(d.unidad_privada);

        return {
          ...d,
          saldo_calculado: tot,
          targetStage,
          stageCode,
          recommendedChannel,
          priority,
          isSelected
        };
      });
  }, [registeredDebtors, selectedDebtorKeys]);

  // Lista filtrada de candidatos para vista de tabla
  const filteredCandidates = useMemo(() => {
    return dryRunCandidates.filter((d) => {
      // Filtro de texto
      if (debtorSearchQuery.trim()) {
        const q = debtorSearchQuery.toLowerCase();
        const matchesUnit = d.unidad_privada.toLowerCase().includes(q);
        const matchesName = d.propietario_nombre.toLowerCase().includes(q);
        const matchesPhone = d.telefono ? d.telefono.includes(q) : false;
        if (!matchesUnit && !matchesName && !matchesPhone) return false;
      }
      // Filtro por etapa
      if (debtorStageFilter === 'preventivo' && (d.dias_mora > 30 || d.dias_mora < 1)) return false;
      if (debtorStageFilter === 'prejuridico' && (d.dias_mora < 31 || d.dias_mora > 60)) return false;
      if (debtorStageFilter === 'prejudicial' && d.dias_mora <= 60) return false;
      return true;
    });
  }, [dryRunCandidates, debtorSearchQuery, debtorStageFilter]);

  // Candidatos efectivamente seleccionados
  const selectedCandidatesList = useMemo(() => {
    return dryRunCandidates.filter((c) => selectedDebtorKeys.has(c.unidad_privada));
  }, [dryRunCandidates, selectedDebtorKeys]);

  const selectedTotalDebt = useMemo(() => {
    return selectedCandidatesList.reduce((acc, c) => acc + c.saldo_calculado, 0);
  }, [selectedCandidatesList]);

  // Candidatos segmentados por nodo del flujo de cobranza
  const nodeCandidates = useMemo(() => {
    return {
      node1: dryRunCandidates.filter((d) => d.dias_mora <= 30 && d.dias_mora >= 1),
      node2: dryRunCandidates.filter((d) => d.dias_mora >= 31 && d.dias_mora <= 60),
      node3: dryRunCandidates.filter((d) => d.dias_mora >= 61 && d.dias_mora <= 90),
      node4: dryRunCandidates.filter((d) => d.dias_mora > 90)
    };
  }, [dryRunCandidates]);

  // Manejo de checkboxes de selección
  const toggleSelectDebtor = (unidadPrivada: string) => {
    setSelectedDebtorKeys((prev) => {
      const next = new Set(prev);
      if (next.has(unidadPrivada)) {
        next.delete(unidadPrivada);
      } else {
        next.add(unidadPrivada);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCandidatesList.length === dryRunCandidates.length) {
      // Deseleccionar todos
      setSelectedDebtorKeys(new Set());
    } else {
      // Seleccionar todos los candidatos
      setSelectedDebtorKeys(new Set(dryRunCandidates.map((c) => c.unidad_privada)));
    }
  };

  // Selección de copropiedad desde el dropdown
  const handleSelectCopropiedad = (name: string) => {
    setSelectedCopropiedad(name);
    if (name === '__new__') {
      setLocalSettings({
        copropiedad_id: `ph_${Date.now()}`,
        nombre_copropiedad: '',
        nit: '',
        administrador_nombre: settings.administrador_nombre || 'Administración PH',
        email_contacto: settings.email_contacto || '',
        telefono_contacto: settings.telefono_contacto || '',
        cuenta_bancaria_info: '',
        dia_corte_mes: 10,
        frecuencia_notificacion: 'quincenal',
        umbrales_dias_mora: [5, 30, 60, 90],
        canales_habilitados: ['email', 'whatsapp'],
        respetar_ley_2300: true,
        tasa_interes_tipo: 'maxima_legal',
        modo_seleccion_propietarios: 'todos',
        propietarios_seleccionados: []
      });
      return;
    }

    const found = savedSchedules.find(
      (s) => s.nombre_copropiedad.trim().toLowerCase() === name.trim().toLowerCase()
    );
    if (found) {
      setLocalSettings(found);
      if (found.propietarios_seleccionados && found.propietarios_seleccionados.length > 0) {
        setSelectedDebtorKeys(new Set(found.propietarios_seleccionados));
      }
      if (found.modo_seleccion_propietarios) {
        setModoSeleccion(found.modo_seleccion_propietarios);
      }
    } else {
      setLocalSettings((prev) => ({
        ...prev,
        nombre_copropiedad: name
      }));
    }
  };

  // Cargar una copropiedad de la tabla en el formulario
  const handleLoadScheduleIntoForm = (item: SchedulerSettings) => {
    setSelectedCopropiedad(item.nombre_copropiedad);
    setLocalSettings(item);
    if (item.propietarios_seleccionados && item.propietarios_seleccionados.length > 0) {
      setSelectedDebtorKeys(new Set(item.propietarios_seleccionados));
    }
    if (item.modo_seleccion_propietarios) {
      setModoSeleccion(item.modo_seleccion_propietarios);
    }
    setActiveSubTab('config');
    toast.info(`Parámetros de "${item.nombre_copropiedad}" cargados para edición.`);
  };

  // Eliminar una programación de la tabla
  const handleDeleteSchedule = (name: string) => {
    if (!window.confirm(`¿Está seguro de remover la programación registrada para "${name}"?`)) return;
    setSavedSchedules((prev) => {
      const updated = prev.filter((s) => s.nombre_copropiedad !== name);
      localStorage.setItem(SCHEDULES_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
    toast.success(`Programación de "${name}" eliminada.`);
  };

  // Guardar estado del switch de motor en localStorage
  const toggleEngine = (active: boolean) => {
    setIsEngineActive(active);
    localStorage.setItem('scheduler_engine_active', String(active));
    if (active) {
      toast.success('Motor de Cobranza Programada ACTIVADO. Las alertas se evaluarán según la cadencia legal.');
    } else {
      toast.info('Motor de Cobranza Programada en PAUSA. No se despacharán mensajes automáticos.');
    }
  };

  // Disparar Guardado de Parámetros
  const handleSave = async (overrideSelectedDebtors?: string[]) => {
    if (!localSettings.nombre_copropiedad.trim()) {
      toast.error('Ingrese o seleccione el nombre de la copropiedad.');
      return;
    }
    setIsSaving(true);
    const updatedSettings: SchedulerSettings = {
      ...localSettings,
      modo_seleccion_propietarios: modoSeleccion,
      propietarios_seleccionados: overrideSelectedDebtors || Array.from(selectedDebtorKeys)
    };

    try {
      await onSaveSettings(updatedSettings);
      setSavedSchedules((prev) => {
        const idx = prev.findIndex(
          (s) =>
            s.nombre_copropiedad.trim().toLowerCase() ===
            updatedSettings.nombre_copropiedad.trim().toLowerCase()
        );
        let updated: SchedulerSettings[];
        if (idx >= 0) {
          updated = [...prev];
          updated[idx] = { ...updatedSettings };
        } else {
          updated = [{ ...updatedSettings }, ...prev];
        }
        try {
          localStorage.setItem(SCHEDULES_STORAGE_KEY, JSON.stringify(updated));
        } catch (e) {
          console.error(e);
        }
        return updated;
      });
      setLocalSettings(updatedSettings);
      setSelectedCopropiedad(updatedSettings.nombre_copropiedad);
      toast.success(
        `Parámetros y selección de propietarios de "${updatedSettings.nombre_copropiedad}" guardados exitosamente.`
      );
    } catch (e: any) {
      toast.error('Error guardando configuración.');
    } finally {
      setIsSaving(false);
    }
  };

  // Ejecutar Disparo desde el Simulador con solo los seleccionados
  const handleExecuteScheduledBatch = async () => {
    if (selectedCandidatesList.length === 0) {
      toast.error('Seleccione al menos un propietario para despachar la campaña.');
      return;
    }

    if (localSettings.respetar_ley_2300 && !legalWindowStatus.isOpen) {
      toast.error(
        `Operación bloqueada por Ley 2300 de 2023: ${legalWindowStatus.windowText}. Se reanudará ${legalWindowStatus.nextOpening}.`
      );
      return;
    }

    setIsExecutingNow(true);
    try {
      if (onTriggerBatchSend) {
        await onTriggerBatchSend(selectedCandidatesList);
      } else {
        toast.success(
          `Campaña de ${selectedCandidatesList.length} requerimientos despachada con éxito.`
        );
      }
      setIsDryRunOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Error al ejecutar campaña.');
    } finally {
      setIsExecutingNow(false);
    }
  };

  // Propietario objetivo para el simulador de mensajes interactivo
  const activeMessageDebtorData = useMemo(() => {
    if (messageDebtorSelected !== 'sample') {
      const found = registeredDebtors.find((d) => d.unidad_privada === messageDebtorSelected);
      if (found) {
        return {
          copropiedad_nombre: localSettings.nombre_copropiedad || 'Conjunto Residencial Torres del Parque',
          nit: localSettings.nit || '900.852.147-3',
          propietario_nombre: found.propietario_nombre,
          unidad_privada: found.unidad_privada,
          saldo_capital: found.saldo_capital,
          saldo_intereses: found.saldo_intereses,
          saldo_total: found.saldo_total || (found.saldo_capital + found.saldo_intereses),
          dias_mora: found.dias_mora,
          payment_link: found.payment_link || 'https://api.miasistentelegalia.com/portal-deudor',
          cuenta_bancaria_info: localSettings.cuenta_bancaria_info || 'Bancolombia Cuenta Corriente No. 123-456789-00',
          administrador_nombre: localSettings.administrador_nombre || 'Administración PH',
          email_contacto: localSettings.email_contacto || 'administracion@torresdelparque.com',
          telefono_contacto: localSettings.telefono_contacto || '3001234567'
        };
      }
    }

    // Datos de ejemplo representativos
    return {
      copropiedad_nombre: localSettings.nombre_copropiedad || 'Conjunto Residencial Torres del Parque',
      nit: localSettings.nit || '900.852.147-3',
      propietario_nombre: 'Carlos Eduardo Restrepo',
      unidad_privada: 'Apto 402 - Torre B',
      saldo_capital: 2100000,
      saldo_intereses: 280000,
      saldo_total: 2380000,
      dias_mora: 45,
      payment_link: 'https://api.miasistentelegalia.com/portal-deudor?id=pago_402',
      cuenta_bancaria_info: localSettings.cuenta_bancaria_info || 'Bancolombia Cuenta Corriente No. 123-456789-00',
      administrador_nombre: localSettings.administrador_nombre || 'Administración PH',
      email_contacto: localSettings.email_contacto || 'administracion@torresdelparque.com',
      telefono_contacto: localSettings.telefono_contacto || '3001234567'
    };
  }, [messageDebtorSelected, registeredDebtors, localSettings]);

  // Mensaje generado para la pestaña de Mensajes
  const activeTemplatePreview = useMemo(() => {
    return generateCarteraPhMessageTemplate(selectedMessageStage, activeMessageDebtorData);
  }, [selectedMessageStage, activeMessageDebtorData]);

  // Función para copiar texto al portapapeles
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Mensaje copiado al portapapeles.');
  };

  return (
    <div className="space-y-4">
      {/* ── 1. HERO ENGINE STATUS & CONTROL BAR (DISEÑO CLARO Y ELEGANTE) ── */}
      <Card className="border border-border bg-card shadow-xs overflow-hidden">
        <CardContent className="p-4 sm:p-5 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20">
                  <Cpu className="h-4 w-4" />
                </div>
                <h2 className="text-base font-semibold tracking-tight text-foreground flex items-center gap-2">
                  Motor de Cobranza Programada &amp; Workflows PH
                </h2>
                <Badge
                  variant="outline"
                  className={`text-[11px] px-2 py-0.5 font-medium ${
                    isEngineActive
                      ? 'border-emerald-500/50 text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30'
                      : 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/50'
                  }`}
                >
                  <span className="relative flex h-1.5 w-1.5 mr-1.5">
                    <span
                      className={`inline-flex rounded-full h-1.5 w-1.5 ${
                        isEngineActive ? 'bg-emerald-500' : 'bg-slate-400'
                      }`}
                    />
                  </span>
                  {isEngineActive ? 'Worker Activo (Beat Online)' : 'Scheduler en Pausa'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground max-w-2xl leading-relaxed">
                Orquestador automático de cobranzas Ley 675 / Ley 2300. Evalúa periódicamente la cartera, calcula mora con tasas SFC y enruta notificaciones según colas de prioridad.
              </p>
            </div>

            {/* Acciones Rápidas del Motor */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDryRunOpen(true)}
                className="bg-background hover:bg-muted text-foreground border-border text-xs h-8 gap-1.5 font-medium shadow-none"
              >
                <Zap className="h-3.5 w-3.5 text-amber-500 fill-amber-500/20" />
                Simular Ejecución (Dry-Run)
              </Button>

              <div className="flex items-center gap-2 bg-muted/40 border border-border px-2.5 py-1 rounded-md">
                <span className="text-xs text-foreground font-medium">Motor:</span>
                <Switch
                  checked={isEngineActive}
                  onCheckedChange={toggleEngine}
                  className="data-[state=checked]:bg-emerald-600"
                />
              </div>
            </div>
          </div>

          {/* Métricas Operativas en Vivo (Línea compacta clara) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-3 border-t border-border text-xs">
            <div className="p-2.5 rounded-lg bg-muted/30 border border-border space-y-0.5">
              <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-primary" />
                Próximo Disparo
              </div>
              <div className="font-semibold text-foreground font-mono text-sm">
                {isEngineActive ? `Mañana ${cronHour} COT` : 'En Pausa'}
              </div>
              <div className="text-[10px] text-muted-foreground">Cadencia: Umbrales y Corte</div>
            </div>

            <div className="p-2.5 rounded-lg bg-muted/30 border border-border space-y-0.5">
              <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-primary" />
                Candidatos en Fila
              </div>
              <div className="font-semibold text-foreground text-sm">
                {selectedCandidatesList.length} de {dryRunCandidates.length} seleccionados
              </div>
              <div className="text-[10px] text-muted-foreground font-mono">
                ${selectedTotalDebt.toLocaleString('es-CO')} COP en riesgo
              </div>
            </div>

            <div className="p-2.5 rounded-lg bg-muted/30 border border-border space-y-0.5">
              <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                Ventana Ley 2300
              </div>
              <div
                className={`font-semibold text-sm flex items-center gap-1.5 ${
                  legalWindowStatus.isOpen ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                }`}
              >
                <span
                  className={`inline-block w-2 h-2 rounded-full ${
                    legalWindowStatus.isOpen ? 'bg-emerald-500' : 'bg-amber-500'
                  }`}
                />
                {legalWindowStatus.isOpen ? 'Permitida' : 'Restringida'}
              </div>
              <div className="text-[10px] text-muted-foreground truncate">{legalWindowStatus.nextOpening}</div>
            </div>

            <div className="p-2.5 rounded-lg bg-muted/30 border border-border space-y-0.5">
              <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5">
                <Server className="h-3.5 w-3.5 text-primary" />
                Canales Activos
              </div>
              <div className="font-semibold text-foreground text-sm">WhatsApp + Email</div>
              <div className="text-[10px] text-muted-foreground">Pasarela Wompi/PSE activa</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── 2. SUB-NAVEGACIÓN DE PESTAÑAS (ORGANIZACIÓN LIMPIA SIN FILAS APILADAS) ── */}
      <Tabs value={activeSubTab} onValueChange={(val: any) => setActiveSubTab(val)} className="space-y-4">
        <div className="bg-card border rounded-lg p-1 shadow-xs">
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto gap-1 bg-transparent p-0">
            <TabsTrigger
              value="config"
              className="text-xs py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5 font-medium"
            >
              <Sliders className="h-3.5 w-3.5" />
              <span>Programación &amp; Reglas</span>
            </TabsTrigger>
            <TabsTrigger
              value="debtors"
              className="text-xs py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5 font-medium"
            >
              <Users className="h-3.5 w-3.5" />
              <span>Propietarios en Cron ({selectedCandidatesList.length})</span>
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              className="text-xs py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5 font-medium"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              <span>Mensajes Notificados</span>
            </TabsTrigger>
            <TabsTrigger
              value="pipeline"
              className="text-xs py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-1.5 font-medium"
            >
              <GitBranch className="h-3.5 w-3.5" />
              <span>Flujo de Cobranza (Pipeline)</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            PESTAÑA 1: PROGRAMACIÓN & PARÁMETROS DE LA COPROPIEDAD
           ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="config" className="space-y-4 mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Formulario de Ajustes */}
            <Card className="lg:col-span-7 shadow-xs border">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Sliders className="h-4 w-4 text-primary" />
                  Parámetros de Frecuencia y Cadencia de Corte
                </CardTitle>
                <CardDescription className="text-xs">
                  Configure los días de corte, periodicidad de despacho y cuenta bancaria de recaudo.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-4 space-y-3.5 text-xs">
                {/* Selector Dropdown de Copropiedad / PH */}
                <div className="space-y-1.5 p-3 rounded-lg border bg-muted/20">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      Seleccionar Copropiedad / PH
                    </Label>
                    <span className="text-[11px] text-muted-foreground">
                      {savedSchedules.length} registrada{savedSchedules.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <Select
                    value={selectedCopropiedad}
                    onValueChange={handleSelectCopropiedad}
                  >
                    <SelectTrigger className="h-8 text-xs bg-background">
                      <SelectValue placeholder="Seleccione un conjunto o copropiedad..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCopropiedades.map((phName) => {
                        const isConfigured = savedSchedules.some(
                          (s) => s.nombre_copropiedad.trim().toLowerCase() === phName.trim().toLowerCase()
                        );
                        return (
                          <SelectItem key={phName} value={phName}>
                            <div className="flex items-center justify-between gap-3 w-full">
                              <span>{phName}</span>
                              {isConfigured && (
                                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium ml-2">
                                  (Programada)
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        );
                      })}
                      <SelectItem value="__new__" className="text-primary font-medium">
                        + Configurar Nueva Copropiedad / PH
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Nombre de la Copropiedad / PH</Label>
                    <Input
                      value={localSettings.nombre_copropiedad}
                      onChange={(e) => {
                        const val = e.target.value;
                        setLocalSettings({ ...localSettings, nombre_copropiedad: val });
                        setSelectedCopropiedad(val);
                      }}
                      placeholder="Ej: Conjunto Residencial Torres del Parque"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">NIT de la Copropiedad</Label>
                    <Input
                      value={localSettings.nit}
                      onChange={(e) => setLocalSettings({ ...localSettings, nit: e.target.value })}
                      placeholder="Ej: 900.123.456-7"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Día de Corte Mensual</Label>
                    <Input
                      type="number"
                      min={1}
                      max={31}
                      value={localSettings.dia_corte_mes}
                      onChange={(e) =>
                        setLocalSettings({
                          ...localSettings,
                          dia_corte_mes: parseInt(e.target.value, 10) || 10
                        })
                      }
                      className="h-8 text-xs font-mono"
                    />
                    <span className="text-[10px] text-muted-foreground">Ej: 10 de cada mes</span>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Frecuencia Notificaciones</Label>
                    <Select
                      value={localSettings.frecuencia_notificacion}
                      onValueChange={(val) =>
                        setLocalSettings({ ...localSettings, frecuencia_notificacion: val })
                      }
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="semanal">Semanal (Cada 7 días)</SelectItem>
                        <SelectItem value="quincenal">Quincenal (Días 15 y 30)</SelectItem>
                        <SelectItem value="mensual">Mensual (Día de corte)</SelectItem>
                        <SelectItem value="umbrales">Por Umbrales de Mora (5, 30, 60 d)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Hora de Disparo (COT)</Label>
                    <Input
                      type="time"
                      value={cronHour}
                      onChange={(e) => setCronHour(e.target.value)}
                      className="h-8 text-xs font-mono"
                    />
                    <span className="text-[10px] text-muted-foreground">Dentro de horario hábil</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium">Información Bancaria para Recaudo</Label>
                  <Textarea
                    rows={2}
                    value={localSettings.cuenta_bancaria_info}
                    onChange={(e) =>
                      setLocalSettings({ ...localSettings, cuenta_bancaria_info: e.target.value })
                    }
                    className="text-xs resize-none"
                    placeholder="Banco, tipo de cuenta, número y titular..."
                  />
                </div>

                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-[11px] text-muted-foreground">
                    Al guardar, los parámetros y propietarios seleccionados quedarán registrados.
                  </span>
                  <Button
                    onClick={() => handleSave()}
                    disabled={isSaving}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-8 gap-1.5 font-medium"
                  >
                    {isSaving ? (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Guardando...
                      </>
                    ) : (
                      <>
                        <Check className="h-3.5 w-3.5" /> Guardar Parámetros
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Auditoría Legal Ley 2300 de 2023 */}
            <Card className="lg:col-span-5 shadow-xs border">
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2 text-foreground">
                    <ShieldAlert className="h-4 w-4 text-primary" />
                    Blindaje Ley 2300 (&quot;Dejen de Fregar&quot;)
                  </CardTitle>
                  <Badge variant="outline" className="text-[10px] font-medium">Auditoría Activa</Badge>
                </div>
                <CardDescription className="text-xs">
                  Garantiza el estricto cumplimiento del marco legal colombiano para prevenir sanciones de la SIC.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-4 space-y-3 text-xs">
                <div className="flex items-center justify-between p-2.5 rounded-lg border bg-card">
                  <div className="space-y-0.5">
                    <span className="font-semibold text-xs text-foreground">
                      Restricción Estricta de Horarios
                    </span>
                    <p className="text-[10px] text-muted-foreground">
                      Bloquea despachos fuera de lunes a viernes 7am-7pm y sábados 8am-3pm.
                    </p>
                  </div>
                  <Switch
                    checked={localSettings.respetar_ley_2300}
                    onCheckedChange={(checked) =>
                      setLocalSettings({ ...localSettings, respetar_ley_2300: checked })
                    }
                  />
                </div>

                {/* Reloj de Estado en Vivo */}
                <div className="p-3 rounded-lg border bg-muted/20 space-y-2">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-medium text-muted-foreground">Estado Actual de la Ventana:</span>
                    <span
                      className={`font-semibold flex items-center gap-1 ${
                        legalWindowStatus.isOpen
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-amber-600 dark:text-amber-400'
                      }`}
                    >
                      {legalWindowStatus.isOpen ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Abierta
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="h-3.5 w-3.5" /> Cerrada
                        </>
                      )}
                    </span>
                  </div>

                  <p className="text-[10px] font-mono bg-background p-2 rounded border text-muted-foreground">
                    {legalWindowStatus.windowText}
                  </p>

                  <div className="text-[10px] text-muted-foreground flex items-center justify-between pt-1">
                    <span>Tope semanal:</span>
                    <strong className="text-foreground">Máx. 2 contactos semanales / canal</strong>
                  </div>
                </div>

                <div className="text-[11px] bg-muted/30 p-2.5 rounded-lg border space-y-1">
                  <div className="font-medium text-foreground flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    Seguridad Jurídica para el Administrador
                  </div>
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    Cada mensaje enviado por WhatsApp o Correo registra automáticamente su marca de tiempo UTC-5 e identificador de entrega para responder ante cualquier requerimiento de la SIC.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabla de Copropiedades Programadas */}
          <Card className="shadow-xs border border-border">
            <CardHeader className="pb-3 border-b">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-sm font-semibold flex items-center gap-2 text-foreground">
                    <Calendar className="h-4 w-4 text-primary" />
                    Copropiedades Programadas &amp; Cadencia de Cobranza
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Resumen de parámetros configurados para cada conjunto residencial o copropiedad.
                  </CardDescription>
                </div>
                <Badge variant="outline" className="text-xs font-medium">
                  {savedSchedules.length}{' '}
                  {savedSchedules.length === 1 ? 'copropiedad registrada' : 'copropiedades registradas'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs bg-muted/30">
                      <TableHead className="font-semibold">Copropiedad / PH</TableHead>
                      <TableHead className="font-semibold">NIT</TableHead>
                      <TableHead className="font-semibold">Día de Corte</TableHead>
                      <TableHead className="font-semibold">Frecuencia</TableHead>
                      <TableHead className="font-semibold">Hora Disparo</TableHead>
                      <TableHead className="font-semibold">Canales</TableHead>
                      <TableHead className="font-semibold">Blindaje Ley 2300</TableHead>
                      <TableHead className="text-right font-semibold">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {savedSchedules.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-xs text-muted-foreground">
                          No hay copropiedades programadas aún. Ingrese los datos arriba y presione &quot;Guardar Parámetros&quot;.
                        </TableCell>
                      </TableRow>
                    ) : (
                      savedSchedules.map((item, idx) => (
                        <TableRow key={idx} className="text-xs hover:bg-muted/40 transition-colors">
                          <TableCell className="font-medium text-foreground">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span>{item.nombre_copropiedad || 'Sin nombre'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-muted-foreground">
                            {item.nit || 'Sin NIT'}
                          </TableCell>
                          <TableCell className="font-mono">Día {item.dia_corte_mes || 10}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-[11px] font-normal capitalize">
                              {item.frecuencia_notificacion || 'quincenal'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-muted-foreground">
                            {cronHour} COT
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {item.canales_habilitados?.includes('whatsapp') && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">WA</Badge>
                              )}
                              {item.canales_habilitados?.includes('email') && (
                                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Email</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {item.respetar_ley_2300 !== false ? (
                              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                                <ShieldCheck className="h-3.5 w-3.5" /> Activo
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                                Inactivo
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleLoadScheduleIntoForm(item)}
                                className="h-7 px-2 text-xs"
                                title="Cargar en el formulario para editar"
                              >
                                <Sliders className="h-3 w-3 mr-1" />
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSchedule(item.nombre_copropiedad)}
                                className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                                title="Eliminar de la lista"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════════════════
            PESTAÑA 2: SELECCIÓN DE PROPIETARIOS EN EL CRON JOB (RESPONDE REQ 3)
           ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="debtors" className="space-y-4 mt-0">
          <Card className="shadow-xs border border-border">
            <CardHeader className="pb-3 border-b">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="space-y-1">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    Propietarios Incluidos en el Disparo Automático (Cron Job)
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Seleccione con las casillas de verificación cuáles copropietarios deben ser incluidos en la ejecución del cron.
                  </CardDescription>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAll}
                    className="text-xs h-8 gap-1"
                  >
                    {selectedCandidatesList.length === dryRunCandidates.length ? (
                      <>Deseleccionar Todos</>
                    ) : (
                      <>Seleccionar Todos ({dryRunCandidates.length})</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSave()}
                    disabled={isSaving}
                    className="text-xs h-8 gap-1.5 border-primary/40 text-primary hover:bg-primary/5"
                  >
                    <Check className="h-3.5 w-3.5" />
                    Guardar Selección en el Cron
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setIsDryRunOpen(true)}
                    className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8 gap-1.5 font-medium"
                  >
                    <Zap className="h-3.5 w-3.5" />
                    Simular con Seleccionados ({selectedCandidatesList.length})
                  </Button>
                </div>
              </div>

              {/* Barra de Modo de Selección & Resumen */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 mt-2 border-t text-xs">
                <div className="p-2.5 rounded-lg border bg-muted/20 flex items-center justify-between">
                  <div>
                    <span className="font-semibold block text-foreground">Modo de Inclusión</span>
                    <span className="text-[10px] text-muted-foreground">
                      {modoSeleccion === 'todos'
                        ? 'Todos los morosos (>5d) entran al cron'
                        : 'Solo los propietarios marcados abajo'}
                    </span>
                  </div>
                  <Select
                    value={modoSeleccion}
                    onValueChange={(val: any) => setModoSeleccion(val)}
                  >
                    <SelectTrigger className="w-[120px] h-7 text-xs bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Automático</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-2.5 rounded-lg border bg-muted/20 flex items-center justify-between">
                  <div>
                    <span className="font-semibold block text-foreground">Unidades Marcadas</span>
                    <span className="text-[10px] text-muted-foreground">Para el próximo ciclo</span>
                  </div>
                  <Badge variant="outline" className="font-mono text-xs font-semibold px-2">
                    {selectedCandidatesList.length} / {dryRunCandidates.length}
                  </Badge>
                </div>

                <div className="p-2.5 rounded-lg border bg-muted/20 flex items-center justify-between">
                  <div>
                    <span className="font-semibold block text-foreground">Cartera en Riesgo Marcada</span>
                    <span className="text-[10px] text-muted-foreground">Suma de saldos deudor</span>
                  </div>
                  <span className="font-mono font-bold text-xs text-foreground">
                    ${selectedTotalDebt.toLocaleString('es-CO')} COP
                  </span>
                </div>
              </div>

              {/* Filtros rápidos y búsqueda */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 pt-2">
                <div className="relative flex-1 max-w-sm">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por unidad, propietario o teléfono..."
                    value={debtorSearchQuery}
                    onChange={(e) => setDebtorSearchQuery(e.target.value)}
                    className="h-8 text-xs pl-8 bg-background"
                  />
                </div>

                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
                  <Button
                    size="sm"
                    variant={debtorStageFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setDebtorStageFilter('all')}
                    className="h-7 text-xs px-2.5"
                  >
                    Todos ({dryRunCandidates.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={debtorStageFilter === 'preventivo' ? 'default' : 'outline'}
                    onClick={() => setDebtorStageFilter('preventivo')}
                    className="h-7 text-xs px-2.5 text-emerald-700 dark:text-emerald-400"
                  >
                    Preventivo (1-30d)
                  </Button>
                  <Button
                    size="sm"
                    variant={debtorStageFilter === 'prejuridico' ? 'default' : 'outline'}
                    onClick={() => setDebtorStageFilter('prejuridico')}
                    className="h-7 text-xs px-2.5 text-amber-700 dark:text-amber-400"
                  >
                    Prejurídico (31-60d)
                  </Button>
                  <Button
                    size="sm"
                    variant={debtorStageFilter === 'prejudicial' ? 'default' : 'outline'}
                    onClick={() => setDebtorStageFilter('prejudicial')}
                    className="h-7 text-xs px-2.5 text-rose-700 dark:text-rose-400"
                  >
                    Prejudicial (&gt;60d)
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs bg-muted/30">
                      <TableHead className="w-[45px] text-center">
                        <Checkbox
                          checked={
                            selectedCandidatesList.length === dryRunCandidates.length &&
                            dryRunCandidates.length > 0
                          }
                          onCheckedChange={toggleSelectAll}
                          aria-label="Seleccionar todos"
                        />
                      </TableHead>
                      <TableHead className="font-semibold">Unidad Privada</TableHead>
                      <TableHead className="font-semibold">Propietario / Contacto</TableHead>
                      <TableHead className="font-semibold">Días Mora</TableHead>
                      <TableHead className="font-semibold">Saldo a Cobrar</TableHead>
                      <TableHead className="font-semibold">Etapa Legal Asignada</TableHead>
                      <TableHead className="font-semibold">Canal Recomendado</TableHead>
                      <TableHead className="text-right font-semibold">Previsualizar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCandidates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-xs text-muted-foreground">
                          No se encontraron copropietarios con los criterios de búsqueda seleccionados.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCandidates.map((c, idx) => {
                        const isChecked = selectedDebtorKeys.has(c.unidad_privada);
                        return (
                          <TableRow
                            key={idx}
                            className={`text-xs transition-colors ${
                              isChecked ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/40 opacity-70'
                            }`}
                          >
                            <TableCell className="text-center">
                              <Checkbox
                                checked={isChecked}
                                onCheckedChange={() => toggleSelectDebtor(c.unidad_privada)}
                                aria-label={`Seleccionar ${c.unidad_privada}`}
                              />
                            </TableCell>
                            <TableCell className="font-semibold text-foreground">
                              {c.unidad_privada}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-foreground">{c.propietario_nombre}</div>
                              <div className="text-[10px] text-muted-foreground font-mono">
                                {c.telefono || c.email || 'Sin contacto'}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono">
                              <span
                                className={`font-bold ${
                                  c.dias_mora > 60
                                    ? 'text-rose-600 dark:text-rose-400'
                                    : c.dias_mora >= 31
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-emerald-600 dark:text-emerald-400'
                                }`}
                              >
                                {c.dias_mora} días
                              </span>
                            </TableCell>
                            <TableCell className="font-mono font-semibold text-foreground">
                              ${c.saldo_calculado.toLocaleString('es-CO')}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`text-[10px] ${
                                  c.dias_mora > 60
                                    ? 'border-rose-300 text-rose-700 bg-rose-50/50 dark:bg-rose-950/30 dark:text-rose-300'
                                    : c.dias_mora >= 31
                                    ? 'border-amber-300 text-amber-700 bg-amber-50/50 dark:bg-amber-950/30 dark:text-amber-300'
                                    : 'border-emerald-300 text-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/30 dark:text-emerald-300'
                                }`}
                              >
                                {c.targetStage}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-[11px]">
                              {c.recommendedChannel}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPreviewDebtor(c)}
                                className="h-7 text-xs px-2 gap-1 text-primary hover:text-primary"
                                title="Ver el mensaje exacto que recibirá este copropietario"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Ver Mensaje</span>
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════════════════
            PESTAÑA 3: MENSAJES NOTIFICADOS & PLANTILLAS (RESPONDE REQ 2)
           ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="messages" className="space-y-4 mt-0">
          <Card className="shadow-xs border border-border">
            <CardHeader className="pb-3 border-b">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-primary" />
                      ¿Qué mensaje se le envía a los propietarios cuando se dispara el cron?
                    </CardTitle>
                    <Badge variant="outline" className="text-[10px] font-medium text-emerald-600 border-emerald-300">
                      Cumplimiento Ley 2300 &amp; Ley 675
                    </Badge>
                  </div>
                  <CardDescription className="text-xs">
                    El motor automatizado adapta el tono y los términos legales según la etapa de mora acumulada del propietario. Inspeccione y copie los formatos oficiales a continuación.
                  </CardDescription>
                </div>

                {/* Selector para simular con datos reales de la copropiedad */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Simular con datos de:</span>
                  <Select
                    value={messageDebtorSelected}
                    onValueChange={setMessageDebtorSelected}
                  >
                    <SelectTrigger className="w-[200px] h-8 text-xs bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sample">Datos de Ejemplo (Apto 402)</SelectItem>
                      {registeredDebtors.map((d) => (
                        <SelectItem key={d.unidad_privada} value={d.unidad_privada}>
                          {d.unidad_privada} - {d.propietario_nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Selector de Etapa de Cobro */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3 mt-1 border-t text-xs">
                <button
                  type="button"
                  onClick={() => setSelectedMessageStage('preventivo')}
                  className={`p-2.5 rounded-lg border text-left transition-all ${
                    selectedMessageStage === 'preventivo'
                      ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/40 text-emerald-900 dark:text-emerald-100 shadow-xs'
                      : 'border-border bg-card hover:bg-muted/30 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-xs text-emerald-600 dark:text-emerald-400">1. Preventiva</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0">1-30 Días</Badge>
                  </div>
                  <p className="text-[11px] leading-snug line-clamp-2">
                    Recordatorio amistoso con botón directo de recaudo PSE y descuento por pronto pago.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedMessageStage('prejuridico')}
                  className={`p-2.5 rounded-lg border text-left transition-all ${
                    selectedMessageStage === 'prejuridico'
                      ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100 shadow-xs'
                      : 'border-border bg-card hover:bg-muted/30 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-xs text-amber-600 dark:text-amber-400">2. Prejurídica</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0">31-60 Días</Badge>
                  </div>
                  <p className="text-[11px] leading-snug line-clamp-2">
                    Requerimiento formal Art. 48 Ley 675 con mérito ejecutivo e intereses de usura SFC.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedMessageStage('prejudicial')}
                  className={`p-2.5 rounded-lg border text-left transition-all ${
                    selectedMessageStage === 'prejudicial'
                      ? 'border-rose-500 bg-rose-50/50 dark:bg-rose-950/40 text-rose-900 dark:text-rose-100 shadow-xs'
                      : 'border-border bg-card hover:bg-muted/30 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-xs text-rose-600 dark:text-rose-400">3. Prejudicial</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0">61-90 Días</Badge>
                  </div>
                  <p className="text-[11px] leading-snug line-clamp-2">
                    Aviso perentorio de 5 días hábiles y advertencia de embargo preventivo de cuentas e inmueble.
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setSelectedMessageStage('judicial')}
                  className={`p-2.5 rounded-lg border text-left transition-all ${
                    selectedMessageStage === 'judicial'
                      ? 'border-purple-500 bg-purple-50/50 dark:bg-purple-950/40 text-purple-900 dark:text-purple-100 shadow-xs'
                      : 'border-border bg-card hover:bg-muted/30 text-muted-foreground'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-xs text-purple-600 dark:text-purple-400">4. Judicial</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0">&gt;90 Días</Badge>
                  </div>
                  <p className="text-[11px] leading-snug line-clamp-2">
                    Traslado al módulo de Cobro Jurídico para radicación de demanda ejecutiva en juzgado.
                  </p>
                </button>
              </div>
            </CardHeader>

            <CardContent className="p-4 space-y-4">
              {/* Información de Fundamento Legal de la Etapa */}
              <div className="p-3 rounded-lg border bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                <div>
                  <span className="font-bold text-foreground block">
                    {activeTemplatePreview.stageName} &bull; Tono: {activeTemplatePreview.tone}
                  </span>
                  <p className="text-[11px] text-muted-foreground">{activeTemplatePreview.legalBasis}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    size="sm"
                    variant={messageChannelView === 'whatsapp' ? 'default' : 'outline'}
                    onClick={() => setMessageChannelView('whatsapp')}
                    className="h-7 text-xs gap-1"
                  >
                    <Smartphone className="h-3 w-3" /> WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant={messageChannelView === 'email' ? 'default' : 'outline'}
                    onClick={() => setMessageChannelView('email')}
                    className="h-7 text-xs gap-1"
                  >
                    <Mail className="h-3 w-3" /> Correo Formal
                  </Button>
                </div>
              </div>

              {/* Previsualización del Canal Seleccionado */}
              {messageChannelView === 'whatsapp' ? (
                /* Burbuja Real de WhatsApp */
                <Card className="border-emerald-300/80 dark:border-emerald-800 shadow-sm overflow-hidden">
                  <CardHeader className="py-2.5 px-4 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex flex-row items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center font-bold text-xs text-white border border-white/30">
                        PH
                      </div>
                      <div>
                        <CardTitle className="text-sm font-bold text-white leading-none">
                          {activeMessageDebtorData.copropiedad_nombre}
                        </CardTitle>
                        <p className="text-[10px] text-emerald-100 flex items-center gap-1 mt-0.5 font-medium">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                          Administración Oficial • WhatsApp Business
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(activeTemplatePreview.whatsapp)}
                        className="h-7 text-xs gap-1 bg-white/10 hover:bg-white/20 text-white border border-white/20"
                      >
                        <Copy className="h-3 w-3" /> Copiar Texto
                      </Button>
                    </div>
                  </CardHeader>

                  <CardContent className="p-4 bg-[#EFEAE2] dark:bg-[#0b141a]">
                    <div className="relative bg-[#D9FDD3] dark:bg-[#005C4B] text-slate-900 dark:text-slate-100 p-4 rounded-2xl rounded-tl-none shadow-md max-w-full text-xs space-y-2 border border-emerald-300/60 dark:border-emerald-700/60 whitespace-pre-wrap font-sans leading-relaxed">
                      {activeTemplatePreview.whatsapp}

                      <div className="pt-2 border-t border-emerald-400/30 flex items-center justify-between text-[10px] text-slate-500 dark:text-emerald-200">
                        <span className="font-medium text-emerald-800 dark:text-emerald-200">
                          Despacho programado Ley 675 / Ley 2300 de 2023
                        </span>
                        <span className="flex items-center gap-1 font-medium">
                          Hoy {cronHour} COT
                          <CheckCheck className="h-3.5 w-3.5 text-sky-500 inline stroke-[2.5]" />
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                /* Previsualización de Correo Electrónico Formal */
                <Card className="border border-border shadow-sm">
                  <CardHeader className="py-2.5 px-4 bg-muted/40 border-b flex flex-row items-center justify-between">
                    <div className="space-y-0.5">
                      <div className="text-[11px] text-muted-foreground">
                        <span className="font-semibold text-foreground">Asunto: </span>
                        {activeTemplatePreview.emailSubject}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        <span className="font-medium">Para: </span>
                        {activeMessageDebtorData.propietario_nombre} &lt;{activeMessageDebtorData.email_contacto}&gt;
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(activeTemplatePreview.emailBody)}
                      className="h-7 text-xs gap-1"
                    >
                      <Copy className="h-3 w-3" /> Copiar Correo
                    </Button>
                  </CardHeader>
                  <CardContent className="p-4 text-xs font-sans whitespace-pre-wrap leading-relaxed text-foreground bg-background">
                    {activeTemplatePreview.emailBody}
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════════════════
            PESTAÑA 4: DIAGRAMA DE FLUJO INTERACTIVO DE COBRANZA & PIPELINE
           ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="pipeline" className="space-y-4 mt-0">
          <Card className="shadow-xs border border-border">
            <CardHeader className="pb-3 border-b">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-foreground">
                      <GitBranch className="h-4 w-4 text-primary" />
                      Diagrama de Flujo de Cobranza &amp; Escalamiento Legal
                    </CardTitle>
                    <Badge variant="outline" className="text-[10px] font-medium text-emerald-600 border-emerald-300">
                      Workflow Pipeline Continuo
                    </Badge>
                  </div>
                  <CardDescription className="text-xs">
                    Recorrido automatizado del motor: clasifica la cartera, evalúa días de mora y escala de forma inteligente conforme a la Ley 675 y Ley 2300 de 2023.
                  </CardDescription>
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="font-mono text-[11px] bg-muted/40">
                    Disparo: {cronHour} COT
                  </Badge>
                  <Badge variant="outline" className="font-mono text-[11px] bg-muted/40">
                    {queueStats.totalDue} morosos en flujo
                  </Badge>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-4 space-y-6">
              {/* ── 1. DIAGRAMA DE FLUJO HORIZONTAL CONECTADO (STEPPER INTERACTIVO) ── */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground px-1 font-medium">
                  <span>Haga clic en cualquier nodo para inspeccionar sus reglas y unidades en fila:</span>
                  <span className="text-[11px]">Progresión temporal continua →</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 relative">
                  {/* NODO 1: DÍAS 1-30 */}
                  <div
                    onClick={() => setSelectedFlowNode('node1')}
                    className={`cursor-pointer rounded-xl border p-3.5 transition-all relative ${
                      selectedFlowNode === 'node1'
                        ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/40 ring-2 ring-emerald-500/30 shadow-sm'
                        : 'border-border bg-card hover:border-emerald-400/60 hover:bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-300 flex items-center justify-center font-bold text-xs">
                        1
                      </span>
                      <Badge variant="outline" className="border-emerald-300 text-emerald-700 dark:text-emerald-400 font-mono text-[10px] bg-emerald-50/50">
                        queue_low &bull; P1
                      </Badge>
                    </div>

                    <div className="space-y-1">
                      <div className="text-[10px] font-mono text-muted-foreground font-semibold">
                        DÍAS 1 A 30 DE CORTE
                      </div>
                      <h4 className="font-semibold text-xs text-foreground">
                        Recordatorio Amistoso &amp; Pronto Pago
                      </h4>
                      <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                        WhatsApp cordial con botón PSE Wompi. Invita a conservar beneficios.
                      </p>
                    </div>

                    <div className="pt-2 mt-2.5 border-t border-border/80 flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Unidades en Cola:</span>
                      <strong className="font-mono text-foreground font-semibold">
                        {queueStats.lowCount} (${queueStats.lowSaldo.toLocaleString('es-CO')})
                      </strong>
                    </div>

                    {/* Indicador de flecha hacia el siguiente nodo */}
                    <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-background border border-border items-center justify-center text-muted-foreground shadow-xs">
                      <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>

                  {/* NODO 2: DÍAS 31-60 */}
                  <div
                    onClick={() => setSelectedFlowNode('node2')}
                    className={`cursor-pointer rounded-xl border p-3.5 transition-all relative ${
                      selectedFlowNode === 'node2'
                        ? 'border-amber-500 bg-amber-50/50 dark:bg-amber-950/40 ring-2 ring-amber-500/30 shadow-sm'
                        : 'border-border bg-card hover:border-amber-400/60 hover:bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300 flex items-center justify-center font-bold text-xs">
                        2
                      </span>
                      <Badge variant="outline" className="border-amber-300 text-amber-700 dark:text-amber-400 font-mono text-[10px] bg-amber-50/50">
                        queue_medium &bull; P5
                      </Badge>
                    </div>

                    <div className="space-y-1">
                      <div className="text-[10px] font-mono text-muted-foreground font-semibold">
                        DÍAS 31 A 60 DE CORTE
                      </div>
                      <h4 className="font-semibold text-xs text-foreground">
                        Requerimiento Art. 48 Ley 675
                      </h4>
                      <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                        Título ejecutivo en PDF con firma y causación de intereses de usura SFC.
                      </p>
                    </div>

                    <div className="pt-2 mt-2.5 border-t border-border/80 flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Unidades en Cola:</span>
                      <strong className="font-mono text-foreground font-semibold">
                        {queueStats.mediumCount} (${queueStats.mediumSaldo.toLocaleString('es-CO')})
                      </strong>
                    </div>

                    {/* Indicador de flecha hacia el siguiente nodo */}
                    <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-background border border-border items-center justify-center text-muted-foreground shadow-xs">
                      <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>

                  {/* NODO 3: DÍAS 61-90 */}
                  <div
                    onClick={() => setSelectedFlowNode('node3')}
                    className={`cursor-pointer rounded-xl border p-3.5 transition-all relative ${
                      selectedFlowNode === 'node3'
                        ? 'border-rose-500 bg-rose-50/50 dark:bg-rose-950/40 ring-2 ring-rose-500/30 shadow-sm'
                        : 'border-border bg-card hover:border-rose-400/60 hover:bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="w-6 h-6 rounded-full bg-rose-100 dark:bg-rose-900/60 text-rose-800 dark:text-rose-300 flex items-center justify-center font-bold text-xs">
                        3
                      </span>
                      <Badge variant="outline" className="border-rose-300 text-rose-700 dark:text-rose-400 font-mono text-[10px] bg-rose-50/50">
                        queue_high &bull; P10
                      </Badge>
                    </div>

                    <div className="space-y-1">
                      <div className="text-[10px] font-mono text-muted-foreground font-semibold">
                        DÍAS 61 A 90 DE CORTE
                      </div>
                      <h4 className="font-semibold text-xs text-foreground">
                        Prejudicial &amp; Aviso Demanda
                      </h4>
                      <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                        Intimación de 5 días. Advertencia de embargo de cuentas e inmueble (Art. 599 CGP).
                      </p>
                    </div>

                    <div className="pt-2 mt-2.5 border-t border-border/80 flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Unidades en Cola:</span>
                      <strong className="font-mono text-foreground font-semibold">
                        {queueStats.highCount} (${queueStats.highSaldo.toLocaleString('es-CO')})
                      </strong>
                    </div>

                    {/* Indicador de flecha hacia el siguiente nodo */}
                    <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-background border border-border items-center justify-center text-muted-foreground shadow-xs">
                      <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>

                  {/* NODO 4: DÍAS 90+ */}
                  <div
                    onClick={() => setSelectedFlowNode('node4')}
                    className={`cursor-pointer rounded-xl border p-3.5 transition-all relative ${
                      selectedFlowNode === 'node4'
                        ? 'border-purple-500 bg-purple-50/50 dark:bg-purple-950/40 ring-2 ring-purple-500/30 shadow-sm'
                        : 'border-border bg-card hover:border-purple-400/60 hover:bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/60 text-purple-800 dark:text-purple-300 flex items-center justify-center font-bold text-xs">
                        4
                      </span>
                      <Badge variant="outline" className="border-purple-300 text-purple-700 dark:text-purple-400 font-mono text-[10px] bg-purple-50/50">
                        Juzgados Civiles
                      </Badge>
                    </div>

                    <div className="space-y-1">
                      <div className="text-[10px] font-mono text-muted-foreground font-semibold">
                        MÁS DE 90 DÍAS
                      </div>
                      <h4 className="font-semibold text-xs text-foreground">
                        Demanda Ejecutiva CGP
                      </h4>
                      <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                        Expediente radicado con poder y medidas cautelares para mandamiento de pago.
                      </p>
                    </div>

                    <div className="pt-2 mt-2.5 border-t border-border/80 flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Destino:</span>
                      <strong className="text-primary font-semibold flex items-center gap-1">
                        Módulo Jurídico <ArrowRight className="h-3 w-3" />
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── 2. BIFURCACIONES DE DECISIÓN DEL FLUJO (DECISION GATES) ── */}
              <div className="p-4 rounded-xl border bg-muted/20 space-y-3">
                <div className="flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-primary" />
                  <h4 className="font-semibold text-xs text-foreground">
                    Bifurcaciones de Decisión &amp; Salidas del Flujo
                  </h4>
                  <span className="text-[11px] text-muted-foreground">
                    (¿Qué ocurre cuando un copropietario interactúa o ignora el cobro?)
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  {/* Salida 1: Pago Exitoso */}
                  <div className="p-3 rounded-lg border bg-card space-y-1.5 border-emerald-200 dark:border-emerald-800/60">
                    <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-semibold">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>Ruta A: Pago Inmediato (PSE / Banco)</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      El deudor cancela a través del enlace Wompi o consigna en la cuenta del conjunto. El sistema emite el Paz y Salvo oficial en PDF y <strong>lo retira inmediatamente del flujo de cobro</strong>.
                    </p>
                    <div className="text-[10px] font-mono text-emerald-700 dark:text-emerald-300 font-medium pt-1 border-t">
                      ✓ Salida Exitosa • Cartera Recuperada
                    </div>
                  </div>

                  {/* Salida 2: Negociación */}
                  <div className="p-3 rounded-lg border bg-card space-y-1.5 border-sky-200 dark:border-sky-800/60">
                    <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400 font-semibold">
                      <Handshake className="h-4 w-4" />
                      <span>Ruta B: Suscripción de Acuerdo</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      El residente ingresa al Portal del Deudor y pacta un plan de cuotas autorizado. El orquestador <strong>congela los intereses de mora punitivos y pausa los requerimientos</strong> mientras cumpla.
                    </p>
                    <div className="text-[10px] font-mono text-sky-700 dark:text-sky-300 font-medium pt-1 border-t">
                      ⏸ Pausa Temporal • Amortización de Pagos
                    </div>
                  </div>

                  {/* Salida 3: Persistencia de Impago */}
                  <div className="p-3 rounded-lg border bg-card space-y-1.5 border-rose-200 dark:border-rose-800/60">
                    <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 font-semibold">
                      <AlertTriangle className="h-4 w-4" />
                      <span>Ruta C: Silencio o Falta de Pago</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      El propietario ignora los mensajes. Al cumplirse la fecha de corte, el Worker Beat a las {cronHour} COT <strong>lo escala automáticamente al siguiente nodo con mayor rigor legal</strong>.
                    </p>
                    <div className="text-[10px] font-mono text-rose-700 dark:text-rose-300 font-medium pt-1 border-t">
                      ➜ Escalamiento Automático de Etapa
                    </div>
                  </div>
                </div>
              </div>

              {/* ── 3. INSPECTOR INTERACTIVO DEL NODO SELECCIONADO ── */}
              <div className="space-y-3 pt-2 border-t">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h4 className="font-semibold text-xs text-foreground flex items-center gap-2">
                      <Layers className="h-4 w-4 text-primary" />
                      Detalle de Operación del Nodo Seleccionado:
                      <span className="font-bold text-primary">
                        {selectedFlowNode === 'node1' && 'Etapa 1: Preventiva & Pronto Pago'}
                        {selectedFlowNode === 'node2' && 'Etapa 2: Prejurídica Formal (Art. 48 Ley 675)'}
                        {selectedFlowNode === 'node3' && 'Etapa 3: Prejudicial & Aviso Demanda'}
                        {selectedFlowNode === 'node4' && 'Etapa 4: Cobro Judicial (Demanda CGP)'}
                      </span>
                    </h4>
                    <p className="text-[11px] text-muted-foreground">
                      {selectedFlowNode === 'node1' && 'Aviso cordial para evitar causación de intereses. Máxima efectividad en los primeros 10 días tras el corte.'}
                      {selectedFlowNode === 'node2' && 'Título ejecutivo formal con causación automática de intereses certificados por la Superfinanciera.'}
                      {selectedFlowNode === 'node3' && 'Intimación perentoria de 5 días hábiles antes de radicar la solicitud de medidas cautelares.'}
                      {selectedFlowNode === 'node4' && 'Expediente digital listo para radicación ante los Juzgados Civiles Municipales.'}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs font-mono font-semibold">
                      {selectedFlowNode === 'node1' && `${nodeCandidates.node1.length} unidades`}
                      {selectedFlowNode === 'node2' && `${nodeCandidates.node2.length} unidades`}
                      {selectedFlowNode === 'node3' && `${nodeCandidates.node3.length} unidades`}
                      {selectedFlowNode === 'node4' && `${nodeCandidates.node4.length} unidades`}
                    </Badge>
                  </div>
                </div>

                {/* Lista de unidades actualmente detenidas en este nodo */}
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs bg-muted/30">
                        <TableHead>Unidad Privada</TableHead>
                        <TableHead>Propietario</TableHead>
                        <TableHead>Días de Mora</TableHead>
                        <TableHead>Saldo a Cobrar</TableHead>
                        <TableHead>Canal Despacho</TableHead>
                        <TableHead className="text-right">Acción</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const currentList =
                          selectedFlowNode === 'node1'
                            ? nodeCandidates.node1
                            : selectedFlowNode === 'node2'
                            ? nodeCandidates.node2
                            : selectedFlowNode === 'node3'
                            ? nodeCandidates.node3
                            : nodeCandidates.node4;

                        if (currentList.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center py-6 text-xs text-muted-foreground">
                                No hay unidades morosas en esta etapa actualmente en {localSettings.nombre_copropiedad || 'la copropiedad'}.
                              </TableCell>
                            </TableRow>
                          );
                        }

                        return currentList.map((c, idx) => (
                          <TableRow key={idx} className="text-xs hover:bg-muted/40">
                            <TableCell className="font-semibold text-foreground">
                              {c.unidad_privada}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{c.propietario_nombre}</div>
                              <div className="text-[10px] text-muted-foreground font-mono">
                                {c.telefono || c.email || 'Sin contacto'}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono">
                              <span
                                className={`font-bold ${
                                  c.dias_mora > 60
                                    ? 'text-rose-600'
                                    : c.dias_mora >= 31
                                    ? 'text-amber-600'
                                    : 'text-emerald-600'
                                }`}
                              >
                                {c.dias_mora} días
                              </span>
                            </TableCell>
                            <TableCell className="font-mono font-semibold">
                              ${c.saldo_calculado.toLocaleString('es-CO')}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-[11px]">
                              {c.recommendedChannel}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPreviewDebtor(c)}
                                className="h-7 text-xs gap-1 text-primary hover:text-primary"
                              >
                                <Eye className="h-3.5 w-3.5" /> Ver Mensaje
                              </Button>
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── 5. MODAL DE SIMULACIÓN (DRY-RUN CON SELECCIÓN DE PROPIETARIOS) ── */}
      <Dialog open={isDryRunOpen} onOpenChange={setIsDryRunOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-base flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-orange-600 text-white shadow-xs">
                  <Zap className="h-4 w-4" />
                </div>
                <span>Simulación de Ejecución Automática (Dry-Run Test)</span>
              </DialogTitle>
              <Badge variant="outline" className="text-xs">
                {selectedCandidatesList.length} de {dryRunCandidates.length} seleccionados
              </Badge>
            </div>
            <DialogDescription className="text-xs">
              Marque o desmarque las casillas de verificación para definir con precisión qué copropietarios serán incluidos en este despacho o en el cron job.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2 overflow-y-auto flex-1">
            {/* Banner de Estado Legal de la Simulación */}
            <div
              className={`p-3 rounded-lg border text-xs flex items-center justify-between ${
                legalWindowStatus.isOpen
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-200'
                  : 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200'
              }`}
            >
              <div className="flex items-center gap-2">
                {legalWindowStatus.isOpen ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                )}
                <div>
                  <span className="font-bold">
                    {legalWindowStatus.isOpen
                      ? 'Ventana hábil disponible para envíos inmediatos.'
                      : 'Atención: Ventana fuera de horario legal Ley 2300.'}
                  </span>
                  <p className="text-[10px] opacity-80">{legalWindowStatus.windowText}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-xs">
                  ${selectedTotalDebt.toLocaleString('es-CO')} COP
                </div>
                <div className="text-[10px] opacity-80">Mora seleccionada</div>
              </div>
            </div>

            {/* Acciones de Selección Rápida en el Modal */}
            <div className="flex items-center justify-between gap-2 px-1 text-xs">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectAll}
                  className="h-7 text-xs px-2.5"
                >
                  {selectedCandidatesList.length === dryRunCandidates.length
                    ? 'Deseleccionar Todos'
                    : 'Seleccionar Todos'}
                </Button>
                <span className="text-[11px] text-muted-foreground">
                  {selectedCandidatesList.length} propietarios seleccionados para el despacho
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleSave()}
                className="h-7 text-xs text-primary"
              >
                Aplicar selección al Cron permanente
              </Button>
            </div>

            {/* Tabla de Candidatos Evaluados con Checkboxes */}
            <div className="rounded-lg border max-h-[300px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs bg-muted/30">
                    <TableHead className="w-[45px] text-center">
                      <Checkbox
                        checked={
                          selectedCandidatesList.length === dryRunCandidates.length &&
                          dryRunCandidates.length > 0
                        }
                        onCheckedChange={toggleSelectAll}
                        aria-label="Seleccionar todos"
                      />
                    </TableHead>
                    <TableHead>Unidad Privada</TableHead>
                    <TableHead>Propietario</TableHead>
                    <TableHead>Días Mora</TableHead>
                    <TableHead>Saldo a Cobrar</TableHead>
                    <TableHead>Etapa Legal</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead className="text-right">Mensaje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dryRunCandidates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-xs text-muted-foreground">
                        No hay unidades con mora suficiente (&gt;5 días) para disparar notificaciones en este ciclo.
                      </TableCell>
                    </TableRow>
                  ) : (
                    dryRunCandidates.map((c, idx) => {
                      const isChecked = selectedDebtorKeys.has(c.unidad_privada);
                      return (
                        <TableRow
                          key={idx}
                          className={`text-xs transition-colors ${
                            isChecked ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/40 opacity-70'
                          }`}
                        >
                          <TableCell className="text-center">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => toggleSelectDebtor(c.unidad_privada)}
                              aria-label={`Seleccionar ${c.unidad_privada}`}
                            />
                          </TableCell>
                          <TableCell className="font-semibold text-slate-900 dark:text-white">
                            {c.unidad_privada}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{c.propietario_nombre}</div>
                            <div className="text-[10px] text-muted-foreground font-mono">
                              {c.telefono || c.email || 'Sin contacto'}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono">
                            <span
                              className={`font-bold ${
                                c.dias_mora > 60
                                  ? 'text-red-600'
                                  : c.dias_mora >= 31
                                  ? 'text-amber-600'
                                  : 'text-emerald-600'
                              }`}
                            >
                              {c.dias_mora} días
                            </span>
                          </TableCell>
                          <TableCell className="font-semibold font-mono">
                            ${c.saldo_calculado.toLocaleString('es-CO')}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={`text-[9px] ${
                                c.dias_mora > 60
                                  ? 'border-red-300 text-red-700'
                                  : c.dias_mora >= 31
                                  ? 'border-amber-300 text-amber-700'
                                  : 'border-emerald-300 text-emerald-700'
                              }`}
                            >
                              {c.targetStage}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[11px] text-muted-foreground">
                            {c.recommendedChannel}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPreviewDebtor(c)}
                              className="h-6 w-6 p-0"
                              title="Ver mensaje personalizado"
                            >
                              <Eye className="h-3.5 w-3.5 text-primary" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-3 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDryRunOpen(false)}
              className="text-xs h-8"
            >
              Cerrar
            </Button>
            <Button
              size="sm"
              onClick={handleExecuteScheduledBatch}
              disabled={isExecutingNow || selectedCandidatesList.length === 0}
              className="bg-orange-600 hover:bg-orange-700 text-white text-xs h-8 gap-1.5 font-semibold"
            >
              {isExecutingNow ? (
                <>
                  <RefreshCw className="h-3 w-3 animate-spin" /> Despachando lote...
                </>
              ) : (
                <>
                  <Play className="h-3 w-3 fill-white" /> Despachar Seleccionados ({selectedCandidatesList.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── 6. MODAL DE PREVIEW INDIVIDUAL DE MENSAJE ── */}
      <Dialog open={Boolean(previewDebtor)} onOpenChange={(open) => !open && setPreviewDebtor(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-primary" />
              <span>
                Mensaje de Cobro &bull; {previewDebtor?.unidad_privada} ({previewDebtor?.propietario_nombre})
              </span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Previsualización del requerimiento legal que recibirá el propietario según sus días de mora ({previewDebtor?.dias_mora} días).
            </DialogDescription>
          </DialogHeader>

          {previewDebtor && (
            <div className="space-y-3 py-2 text-xs">
              <div className="flex items-center justify-between p-2 rounded-lg border bg-muted/20">
                <div>
                  <span className="font-semibold">{previewDebtor.propietario_nombre}</span>
                  <span className="text-[10px] text-muted-foreground block">
                    Unidad {previewDebtor.unidad_privada} &bull; Tel: {previewDebtor.telefono || 'Sin celular'}
                  </span>
                </div>
                <div className="text-right">
                  <span className="font-mono font-bold text-foreground">
                    ${(previewDebtor.saldo_total || previewDebtor.saldo_capital + previewDebtor.saldo_intereses).toLocaleString('es-CO')} COP
                  </span>
                  <Badge variant="outline" className="text-[9px] block mt-0.5">
                    {previewDebtor.dias_mora} Días Mora
                  </Badge>
                </div>
              </div>

              {/* Burbuja WhatsApp */}
              <div className="rounded-lg border overflow-hidden">
                <div className="bg-[#075E54] text-white p-2.5 text-xs flex items-center justify-between">
                  <span className="font-medium">Mensaje WhatsApp Personalizado</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const tmpl = generateCarteraPhMessageTemplate(
                        previewDebtor.dias_mora > 60
                          ? 'prejudicial'
                          : previewDebtor.dias_mora >= 31
                          ? 'prejuridico'
                          : 'preventivo',
                        {
                          copropiedad_nombre: localSettings.nombre_copropiedad || 'Copropiedad',
                          nit: localSettings.nit,
                          propietario_nombre: previewDebtor.propietario_nombre,
                          unidad_privada: previewDebtor.unidad_privada,
                          saldo_capital: previewDebtor.saldo_capital,
                          saldo_intereses: previewDebtor.saldo_intereses,
                          saldo_total: previewDebtor.saldo_total || (previewDebtor.saldo_capital + previewDebtor.saldo_intereses),
                          dias_mora: previewDebtor.dias_mora,
                          payment_link: previewDebtor.payment_link,
                          cuenta_bancaria_info: localSettings.cuenta_bancaria_info,
                          administrador_nombre: localSettings.administrador_nombre,
                          email_contacto: localSettings.email_contacto,
                          telefono_contacto: localSettings.telefono_contacto
                        }
                      );
                      copyToClipboard(tmpl.whatsapp);
                    }}
                    className="h-6 text-[10px] text-white hover:bg-white/10 gap-1 px-2"
                  >
                    <Copy className="h-3 w-3" /> Copiar
                  </Button>
                </div>
                <div className="p-3 bg-[#EFEAE2] dark:bg-[#0b141a]">
                  <div className="bg-[#D9FDD3] dark:bg-[#005C4B] text-slate-900 dark:text-slate-100 p-3 rounded-lg text-xs whitespace-pre-wrap font-sans leading-relaxed border border-emerald-300/50">
                    {
                      generateCarteraPhMessageTemplate(
                        previewDebtor.dias_mora > 60
                          ? 'prejudicial'
                          : previewDebtor.dias_mora >= 31
                          ? 'prejuridico'
                          : 'preventivo',
                        {
                          copropiedad_nombre: localSettings.nombre_copropiedad || 'Copropiedad',
                          nit: localSettings.nit,
                          propietario_nombre: previewDebtor.propietario_nombre,
                          unidad_privada: previewDebtor.unidad_privada,
                          saldo_capital: previewDebtor.saldo_capital,
                          saldo_intereses: previewDebtor.saldo_intereses,
                          saldo_total: previewDebtor.saldo_total || (previewDebtor.saldo_capital + previewDebtor.saldo_intereses),
                          dias_mora: previewDebtor.dias_mora,
                          payment_link: previewDebtor.payment_link,
                          cuenta_bancaria_info: localSettings.cuenta_bancaria_info,
                          administrador_nombre: localSettings.administrador_nombre,
                          email_contacto: localSettings.email_contacto,
                          telefono_contacto: localSettings.telefono_contacto
                        }
                      ).whatsapp
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewDebtor(null)}
              className="text-xs h-8"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CobranzaSchedulerHub;
